import { Button } from '@mui/material'
import clsx from 'clsx'
import { Feature, Polygon } from 'geojson'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useMissionStore from 'store/mission'
import { inside } from 'turf'
import { pointToPolygonMeters } from 'utils/calcFunctions'
import { MISSION_MAP_DRAWING_MODES } from 'utils/constants'
import { ICleaningZonePoint } from 'utils/interfaces'
import { useShallow } from 'zustand/react/shallow'

import style from './ZonePointSettings.module.css'

enum MODES {
  NO_POINT,
  DRAWING,
  DRAWN,
}

interface Props {
  kind: 'starting' | 'ending'
}

const ZonePointSettings = ({ kind }: Props) => {
  const { t } = useTranslation()
  const isStarting = kind === 'starting'
  const i18nPrefix = `zone_details.${isStarting ? 'start_point' : 'end_point'}`
  const addMode = isStarting
    ? MISSION_MAP_DRAWING_MODES.ADD_ZONE_STARTING_POINT
    : MISSION_MAP_DRAWING_MODES.ADD_ZONE_ENDING_POINT

  const {
    setMapMode,
    setTempStartingPoint,
    setTempEndingPoint,
    setEditingStartingPoint,
    setEditingEndingPoint,
    editingZone,
    tempZone,
    mapMode,
    tempStartingPoint,
    tempEndingPoint,
    deleteCleaningZoneStartingPoint,
    deleteCleaningZoneEndingPoint,
  } = useMissionStore(
    useShallow(state => ({
      setMapMode: state.setMapMode,
      setTempStartingPoint: state.setTempStartingPoint,
      setTempEndingPoint: state.setTempEndingPoint,
      setEditingStartingPoint: state.setEditingStartingPoint,
      setEditingEndingPoint: state.setEditingEndingPoint,
      editingZone: state.editingZone,
      tempZone: state.tempZone,
      mapMode: state.mapMode,
      tempStartingPoint: state.tempStartingPoint,
      tempEndingPoint: state.tempEndingPoint,
      deleteCleaningZoneStartingPoint: state.deleteCleaningZoneStartingPoint,
      deleteCleaningZoneEndingPoint: state.deleteCleaningZoneEndingPoint,
    }))
  )

  const zone = tempZone ?? editingZone
  const tempPoint: ICleaningZonePoint | undefined = isStarting ? tempStartingPoint : tempEndingPoint

  const mode = useMemo(() => {
    if (!tempPoint) {
      if (mapMode === addMode) {
        return MODES.DRAWING
      }
      return MODES.NO_POINT
    }
    return MODES.DRAWN
  }, [mapMode, tempPoint, addMode])

  const isInside = useMemo(() => {
    if (!tempPoint || !zone) return false
    try {
      return inside(tempPoint, zone as Feature<Polygon>)
    } catch {
      return false
    }
  }, [tempPoint, zone])

  const distanceToZone = useMemo(() => {
    if (!tempPoint || !zone) return null
    try {
      const polygon = zone as Feature<Polygon>
      const coords = polygon.geometry.coordinates as [number, number][][]
      const point = tempPoint.geometry.coordinates as [number, number]
      return Math.round(pointToPolygonMeters(point, coords))
    } catch {
      return null
    }
  }, [tempPoint, zone])

  const onAddPointClick = () => {
    setMapMode(addMode)
  }

  const onCancelClick = () => {
    setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
  }

  const onDeleteClick = () => {
    const zoneId = zone?.properties?.zone_id
    if (zoneId !== undefined) {
      if (isStarting) {
        deleteCleaningZoneStartingPoint(zoneId)
      } else {
        deleteCleaningZoneEndingPoint(zoneId)
      }
    }
    if (isStarting) {
      setTempStartingPoint(undefined)
      setEditingStartingPoint(undefined)
    } else {
      setTempEndingPoint(undefined)
      setEditingEndingPoint(undefined)
    }
    setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
  }

  const buttons = {
    [MODES.NO_POINT]: (
      <Button
        disabled={!editingZone && !tempZone}
        className={style.button}
        variant="outlined"
        onClick={onAddPointClick}
      >
        {t(`${i18nPrefix}_add_point`)}
      </Button>
    ),
    [MODES.DRAWING]: (
      <Button className={style.button} variant="outlined" onClick={onCancelClick}>
        {t(`${i18nPrefix}_cancel_drawing`)}
      </Button>
    ),
    [MODES.DRAWN]: (
      <Button className={style.button} variant="outlined" onClick={onDeleteClick}>
        {t(`${i18nPrefix}_delete_point`)}
      </Button>
    ),
  }

  const statusInfo = useMemo(() => {
    if (mode !== MODES.DRAWN) {
      return { text: t(`${i18nPrefix}_status`), className: style.statusBar }
    }

    if (isInside) {
      return {
        text: t(`${i18nPrefix}_distance_to_edge`, { distance: distanceToZone ?? 0 }),
        className: `${style.statusBar} ${style.statusBarValid}`,
      }
    }

    if (distanceToZone != null && distanceToZone < 20) {
      return {
        text: t(`${i18nPrefix}_distance`, { distance: distanceToZone }),
        className: `${style.statusBar} ${style.statusBarValid}`,
      }
    }

    return {
      text: t(`${i18nPrefix}_error`, { distance: distanceToZone }),
      className: `${style.statusBar} ${style.statusBarInvalid}`,
    }
  }, [mode, isInside, distanceToZone, t, i18nPrefix])

  return (
    <>
      <div className={style.flex}>
        <div className={clsx('text-14-400', statusInfo.className)}>{statusInfo.text}</div>
        {buttons[mode]}
      </div>
      {mode === MODES.NO_POINT && <div className="text-12-400">{t(`${i18nPrefix}_description`)}</div>}
      {mode === MODES.DRAWN && <div className="text-12-400">{t('zone_details.points_delete_hint')}</div>}
    </>
  )
}

export default ZonePointSettings
